/*
 * File: DeliveryAddressStep.tsx
 * Project: papa-bear
 * -----
 * Last Modified: 30 Dec 2021 9:49:23 am
 * -----
 * Copyright (c) 2021 Elegant Software Solutions All rights reserved.
 */

import { Button, Result, Row, Space, Tooltip, Typography } from 'antd';
import { AddDeliveryAddressModal } from 'components/atoms/createDeliveryAddress/AddDeliveryAddressModal';
import { DeliveryAddressCityFilter } from 'components/atoms/deliveryAddressFilters/deliveryAddressCityFilter';
import { DeliveryAddressDescriptionFilter } from 'components/atoms/deliveryAddressFilters/deliveryAddressDescriptionFilter';
import { DeliveryAddressStateFilter } from 'components/atoms/deliveryAddressFilters/deliveryAddressStateFilter';
import { DeliveryAddressStreetFilter } from 'components/atoms/deliveryAddressFilters/deliveryAddressStreetFilter';
import { DeliveryAddressZipCodeFilter } from 'components/atoms/deliveryAddressFilters/deliveryAddressZipCodeFilter';
import WarningMessage from 'components/atoms/warningMessage/WarningMessage';
import { DeliveryAddressGrid } from 'components/molecules/DeliveryAddressGrid';
import { useFormikContext } from 'formik';
import { InlineStylesModel } from 'models/InlineStylesModel';
import { SalesOrderPayloadModel } from 'models/SalesOrderPayloadModel';
import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

const styles: InlineStylesModel = {
  searchRow: {
    padding: 20,
    backgroundColor: '#b0b2bc',
    width: '100%',
    justifyContent: 'space-between',
    flexWrap: 'wrap'
  },
  searchTitle: {
    marginBottom: 0
  },
  resultsTitleRow: {
    margin: '10px 0px'
  },
  gridRow: {
    // height: '50vh'
  },
  addAddressButton: {
    width: '100%'
  }
};

export const DeliveryAddressStep = (): JSX.Element => {
  const { values } = useFormikContext<SalesOrderPayloadModel>();
  const { id } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [showAddAddressModal, _setShowAddAddressModal] = useState(false);
  const isDisabled = !values.customerName || !values.customerId;

  /* ****************** Functions ****************** */
  const isDraftForm = pathname.includes('/drafts/');
  const isOrderForm = pathname.includes('/orders/');

  const handleGoBack = (): void => {
    if (isDraftForm) {
      navigate(`/drafts/${id}`);
    } else if (isOrderForm) {
      navigate(`/orders/${id}`);
    } else {
      navigate('/create');
    }
  };

  /* ****************** JSX ****************** */
  const addAddressButtonJSX = !isDisabled ? (
    <Tooltip title="Search delivery addresses.">
      <Button type="primary" style={styles.addAddressButton} disabled={isDisabled} onClick={(): void => _setShowAddAddressModal(true)}>
        Add New Address
      </Button>
    </Tooltip>
  ) : (
    <Button type="primary" style={styles.addAddressButton} onClick={(): void => _setShowAddAddressModal(true)}>
      Add New Address
    </Button>
  );

  const deliveryAddressFiltersJSX = (
    <Space>
      <div>Search Filters</div>
      <DeliveryAddressDescriptionFilter isDisabled={isDisabled} />
      <DeliveryAddressStreetFilter isDisabled={isDisabled} />
      <DeliveryAddressCityFilter isDisabled={isDisabled} />
      <DeliveryAddressStateFilter isDisabled={isDisabled} />
      <DeliveryAddressZipCodeFilter isDisabled={isDisabled} />
      {addAddressButtonJSX}
    </Space>
  );

  const warningJSX = !values.customerId ? (
    <Result
      style={{ marginTop: 50 }}
      icon={<WarningMessage />}
      status="warning"
      title="Please select a customer"
      extra={
        <Button type="ghost" key="console" onClick={handleGoBack}>
          Select a Customer
        </Button>
      }
    />
  ) : null;

  /* ****************** Render ****************** */
  return (
    <>
      <AddDeliveryAddressModal
        show={showAddAddressModal}
        onClose={(): void => {
          _setShowAddAddressModal(false);
        }}
      />
      <Space direction="vertical" style={{ width: '100%' }}>
        <Space style={styles.searchRow}>
          <Typography.Title level={4} style={styles.searchTitle}>
            Search Addresses
          </Typography.Title>
          {deliveryAddressFiltersJSX}
        </Space>

        <Row style={styles.gridRow} justify="center" align="middle">
          {warningJSX}
          {Boolean(values.customerId) && <DeliveryAddressGrid />}
        </Row>
      </Space>
    </>
  );
};
